import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { stockyApi } from '../misc/StockyApi';

const StockInconsistencySummaryContext = createContext({
  summary: null,
  loading: false,
  refreshSummary: async () => null,
});

function StockInconsistencySummaryProvider({ children }) {
  const { getUser, userIsAuthenticated } = useAuth();
  const currentUser = getUser();
  const accessToken = currentUser?.accessToken || '';
  const authUser = useMemo(() => (accessToken ? { accessToken } : null), [accessToken]);
  const authenticated = Boolean(currentUser?.accessToken) && userIsAuthenticated();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refreshSummary = useCallback(async ({ silent = false } = {}) => {
    if (!authUser || !authenticated) {
      if (mountedRef.current) {
        setSummary(null);
      }
      return null;
    }

    if (!silent && mountedRef.current) {
      setLoading(true);
    }

    try {
      const response = await stockyApi.getStockInconsistencySummary(authUser, { refresh: 'false' });
      const nextSummary = response?.data || null;
      if (mountedRef.current) {
        setSummary(nextSummary);
      }
      return nextSummary;
    } catch (error) {
      if (mountedRef.current) {
        setSummary(null);
      }
      return null;
    } finally {
      if (mountedRef.current && !silent) {
        setLoading(false);
      }
    }
  }, [authUser, authenticated]);

  useEffect(() => {
    if (!authUser || !authenticated) {
      setSummary(null);
      return undefined;
    }

    refreshSummary();

    const intervalId = window.setInterval(() => {
      refreshSummary({ silent: true });
    }, 120000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshSummary({ silent: true });
      }
    };

    window.addEventListener('focus', handleVisibilityChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleVisibilityChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authUser, authenticated, refreshSummary]);

  const contextValue = useMemo(
    () => ({
      summary,
      loading,
      refreshSummary,
    }),
    [summary, loading, refreshSummary],
  );

  return (
    <StockInconsistencySummaryContext.Provider value={contextValue}>
      {children}
    </StockInconsistencySummaryContext.Provider>
  );
}

function useStockInconsistencySummary() {
  return useContext(StockInconsistencySummaryContext);
}

export {
  StockInconsistencySummaryProvider,
  useStockInconsistencySummary,
};
